import { useState, useEffect, useCallback } from 'react';
import { notificationService } from '@/services/notification.service';
import {
  Notification,
  NotificationListRequest,
  NotificationCreateRequest,
  NotificationUpdateRequest,
  NotificationFilters,
  NotificationPagination,
} from '@/types/notification';

interface UseNotificationsOptions {
  autoRefresh?: boolean;
  refreshInterval?: number;
  initialFilters?: NotificationFilters;
  initialPagination?: NotificationPagination;
}

interface UseNotificationsReturn {
  // State
  notifications: Notification[];
  loading: boolean;
  error: string | null;
  totalCount: number;
  unreadCount: number;
  hasMore: boolean;
  
  // Actions
  fetchNotifications: (request?: NotificationListRequest) => Promise<void>;
  createNotification: (request: NotificationCreateRequest) => Promise<boolean>;
  updateNotification: (request: NotificationUpdateRequest) => Promise<boolean>;
  markAsRead: (id: string) => Promise<boolean>;
  markAllAsRead: (userId?: string) => Promise<boolean>;
  deleteNotification: (id: string) => Promise<boolean>;
  deleteNotifications: (ids: string[]) => Promise<boolean>;
  refreshUnreadCount: () => Promise<void>;
  
  // Filters and pagination
  setFilters: (filters: NotificationFilters) => void;
  setPagination: (pagination: NotificationPagination) => void;
  resetFilters: () => void;
  
  // Utility
  getNotificationById: (id: string) => Notification | undefined;
  formatRelativeTime: (timestamp: Date) => string;
}

export const useNotifications = (options: UseNotificationsOptions = {}): UseNotificationsReturn => {
  const {
    autoRefresh = false,
    refreshInterval = 30000, // 30 seconds
    initialFilters = {},
    initialPagination = { page: 1, limit: 10, sortBy: 'timestamp', sortOrder: 'desc' }
  } = options;

  // State
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [totalCount, setTotalCount] = useState(0);
  const [unreadCount, setUnreadCount] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [filters, setFilters] = useState<NotificationFilters>(initialFilters);
  const [pagination, setPagination] = useState<NotificationPagination>(initialPagination);

  // Fetch notifications - now synchronous since we're using static data
  const fetchNotifications = useCallback(async (request?: NotificationListRequest) => {
    setLoading(true);
    setError(null);

    try {
      const requestData = request || { filters, pagination };
      const response = await notificationService.getNotifications(requestData);

      if (response.succeeded && response.content) {
        setNotifications(response.content.notifications);
        setTotalCount(response.content.totalCount);
        setUnreadCount(response.content.unreadCount);
        setHasMore(response.content.hasMore);
      } else {
        setError(response.messages.join(', ') || 'Failed to fetch notifications');
        setNotifications([]);
        setTotalCount(0);
        setUnreadCount(0);
        setHasMore(false);
      }
    } catch (err: any) {
      setError(err.message || 'An unexpected error occurred');
      setNotifications([]);
      setTotalCount(0);
      setUnreadCount(0);
      setHasMore(false);
    } finally {
      setLoading(false);
    }
  }, [filters, pagination]);

  // Create notification
  const createNotification = useCallback(async (request: NotificationCreateRequest): Promise<boolean> => {
    try {
      const response = await notificationService.createNotification(request);
      
      if (response.succeeded && response.content) {
        // Add the new notification to the beginning of the list
        setNotifications(prev => [response.content!, ...prev]);
        setTotalCount(prev => prev + 1);
        if (!response.content.isRead) {
          setUnreadCount(prev => prev + 1);
        }
        return true;
      } else {
        setError(response.messages.join(', ') || 'Failed to create notification');
        return false;
      }
    } catch (err: any) {
      setError(err.message || 'An unexpected error occurred');
      return false;
    }
  }, []);

  // Update notification
  const updateNotification = useCallback(async (request: NotificationUpdateRequest): Promise<boolean> => {
    try {
      const response = await notificationService.updateNotification(request);
      
      if (response.succeeded && response.content) {
        // Update the notification in the list
        setNotifications(prev => 
          prev.map(notification => 
            notification.id === request.id ? response.content! : notification
          )
        );
        
        // Update unread count if read status changed
        if (request.isRead !== undefined) {
          const wasRead = notifications.find(n => n.id === request.id)?.isRead;
          if (wasRead !== request.isRead) {
            setUnreadCount(prev => request.isRead ? prev - 1 : prev + 1);
          }
        }
        
        return true;
      } else {
        setError(response.messages.join(', ') || 'Failed to update notification');
        return false;
      }
    } catch (err: any) {
      setError(err.message || 'An unexpected error occurred');
      return false;
    }
  }, [notifications]);

  // Mark as read
  const markAsRead = useCallback(async (id: string): Promise<boolean> => {
    try {
      const response = await notificationService.markAsRead(id);
      
      if (response.succeeded) {
        // Update the notification in the list
        setNotifications(prev => 
          prev.map(notification => 
            notification.id === id ? { ...notification, isRead: true } : notification
          )
        );
        
        // Update unread count
        const notification = notifications.find(n => n.id === id);
        if (notification && !notification.isRead) {
          setUnreadCount(prev => prev - 1);
        }
        
        return true;
      } else {
        setError(response.messages.join(', ') || 'Failed to mark notification as read');
        return false;
      }
    } catch (err: any) {
      setError(err.message || 'An unexpected error occurred');
      return false;
    }
  }, [notifications]);

  // Mark all as read
  const markAllAsRead = useCallback(async (userId?: string): Promise<boolean> => {
    try {
      const response = await notificationService.markAllAsRead(userId);
      
      if (response.succeeded) {
        // Update all notifications as read
        setNotifications(prev => 
          prev.map(notification => ({ ...notification, isRead: true }))
        );
        setUnreadCount(0);
        return true;
      } else {
        setError(response.messages.join(', ') || 'Failed to mark all notifications as read');
        return false;
      }
    } catch (err: any) {
      setError(err.message || 'An unexpected error occurred');
      return false;
    }
  }, []);

  // Delete notification
  const deleteNotification = useCallback(async (id: string): Promise<boolean> => {
    try {
      const response = await notificationService.deleteNotification(id);
      
      if (response.succeeded) {
        // Remove the notification from the list
        const notification = notifications.find(n => n.id === id);
        setNotifications(prev => prev.filter(n => n.id !== id));
        setTotalCount(prev => prev - 1);
        
        // Update unread count if the deleted notification was unread
        if (notification && !notification.isRead) {
          setUnreadCount(prev => prev - 1);
        }
        
        return true;
      } else {
        setError(response.messages.join(', ') || 'Failed to delete notification');
        return false;
      }
    } catch (err: any) {
      setError(err.message || 'An unexpected error occurred');
      return false;
    }
  }, [notifications]);

  // Delete multiple notifications
  const deleteNotifications = useCallback(async (ids: string[]): Promise<boolean> => {
    try {
      const response = await notificationService.deleteNotifications(ids);
      
      if (response.succeeded) {
        // Remove the notifications from the list
        const deletedNotifications = notifications.filter(n => ids.includes(n.id));
        const unreadDeleted = deletedNotifications.filter(n => !n.isRead).length;
        
        setNotifications(prev => prev.filter(n => !ids.includes(n.id)));
        setTotalCount(prev => prev - ids.length);
        setUnreadCount(prev => prev - unreadDeleted);
        
        return true;
      } else {
        setError(response.messages.join(', ') || 'Failed to delete notifications');
        return false;
      }
    } catch (err: any) {
      setError(err.message || 'An unexpected error occurred');
      return false;
    }
  }, [notifications]);

  // Refresh unread count
  const refreshUnreadCount = useCallback(async () => {
    try {
      const response = await notificationService.getUnreadCount();
      if (response.succeeded) {
        setUnreadCount(response.content.count);
      }
    } catch (err: any) {
      console.error('Failed to refresh unread count:', err);
    }
  }, []);

  // Reset filters
  const resetFilters = useCallback(() => {
    setFilters(initialFilters);
    setPagination(initialPagination);
  }, [initialFilters, initialPagination]);

  // Get notification by ID
  const getNotificationById = useCallback((id: string): Notification | undefined => {
    return notifications.find(n => n.id === id);
  }, [notifications]);

  // Format relative time
  const formatRelativeTime = useCallback((timestamp: Date): string => {
    return notificationService.formatRelativeTime(timestamp);
  }, []);

  // Auto-refresh effect
  useEffect(() => {
    if (autoRefresh) {
      const interval = setInterval(() => {
        fetchNotifications();
      }, refreshInterval);

      return () => clearInterval(interval);
    }
  }, [autoRefresh, refreshInterval, fetchNotifications]);

  // Initial fetch
  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  return {
    // State
    notifications,
    loading,
    error,
    totalCount,
    unreadCount,
    hasMore,
    
    // Actions
    fetchNotifications,
    createNotification,
    updateNotification,
    markAsRead,
    markAllAsRead,
    deleteNotification,
    deleteNotifications,
    refreshUnreadCount,
    
    // Filters and pagination
    setFilters,
    setPagination,
    resetFilters,
    
    // Utility
    getNotificationById,
    formatRelativeTime,
  };
};